import { ReactComponent as WsgContactCardIcon } from "../../assets/images&icons/wsg-contact-card.svg";
import type { ManageFirmWsgContactsResponse } from "../../interfaces/models/IManageFirmOverview";
import { MANAGE_FIRM_STATS_SECTION_TITLE_CLASS } from "./manageFirmStatsPanelStyles";

function formatCount(value: number | null | undefined): string {
  if (value == null) {
    return "-";
  }
  return value.toLocaleString("en-US");
}

type MetricRow = {
  label: string;
  value: number;
};

function ContactCard({ label, value }: MetricRow): JSX.Element {
  return (
    <div className="relative flex min-h-[8.75rem] flex-col items-center justify-center overflow-hidden rounded-md bg-gray-25 px-4 py-5 text-center">
      <WsgContactCardIcon
        className="pointer-events-none absolute inset-0 m-auto h-[4.8125rem] w-[6.0625rem]"
        aria-hidden
      />
      <span className="relative z-10 text-sm font-medium leading-snug text-gray-font">
        {label}
      </span>
      <span className="relative z-10 mt-2 text-3xl font-semibold tabular-nums text-gray-font">
        {formatCount(value)}
      </span>
    </div>
  );
}

type ManageFirmWsgContactsPanelProps = {
  data: ManageFirmWsgContactsResponse | null | undefined;
  loading?: boolean;
};

export default function ManageFirmWsgContactsPanel({
  data,
  loading = false,
}: ManageFirmWsgContactsPanelProps): JSX.Element {
  if (loading) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col rounded-md border border-gray-200 bg-white p-5 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-48 rounded bg-gray-200" />
          <div className="h-40 rounded bg-gray-100" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col rounded-md border border-gray-200 bg-white p-5 shadow-sm">
        <p className="text-sm text-gray-font">WSG Contacts is unavailable.</p>
      </div>
    );
  }

  const rows: MetricRow[] = [
    {
      label: "Primary Contacts for WSG",
      value: data.primaryContactsForWsg,
    },
    {
      label: "Secondary Contacts for WSG",
      value: data.secondaryContactsForWsg,
    },
    {
      label: "Primary, Secondary & Liaison Contacts for WSG",
      value: data.primarySecondaryLiaisonContactsForWsg,
    },
    {
      label: "Marketing Contacts",
      value: data.marketingContacts,
    },
    {
      label: "IT Contacts",
      value: data.itContacts,
    },
    {
      label: "Accounting Contacts",
      value: data.accountingContacts,
    },
    {
      label: "Social Media Contacts",
      value: data.socialMediaContacts,
    },
    {
      label: "HR Contacts",
      value: data.hrContacts,
    },
    {
      label: "Data Administrators (Main)",
      value: data.dataAdministratorsMain,
    },
    {
      label: "Data Administrators (Branch)",
      value: data.dataAdministratorsBranch,
    },
  ];

  const primaryRows = rows.slice(0, 8);
  const adminRows = rows.slice(8);

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-md border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className={`${MANAGE_FIRM_STATS_SECTION_TITLE_CLASS} mb-5`}>
        WSG Contacts
      </h3>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {primaryRows.map((row) => (
          <ContactCard key={row.label} label={row.label} value={row.value} />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap justify-center gap-3">
        {adminRows.map((row) => (
          <div
            key={row.label}
            className="w-full sm:w-[calc(50%-0.375rem)] lg:w-[calc(25%-0.5625rem)]"
          >
            <ContactCard label={row.label} value={row.value} />
          </div>
        ))}
      </div>
    </div>
  );
}
